/**
 * SeoService.js
 * Comprehensive SEO, GEO (Generative Engine Optimization), and AEO (Answer Engine Optimization) Service
 * Handles Dynamic Meta Tags, Bidirectional hreflang, Canonical Links, and Rich Schema.org JSON-LD (@graph).
 */

import { GeoFaqGenerator } from '../../domain/tourist-center/GeoFaqGenerator.js';

const BASE_URL = 'https://k-tour.kodari.kr';

export class SeoService {
  /**
   * Update full SEO, GEO, AEO, hreflang, and JSON-LD schema for a specific spot or home
   * @param {Object|null} center TouristCenter object (null for home)
   * @param {string} lang 'ko' | 'en' | 'ja' | 'zh'
   */
  static updateSpotSeo(center, lang = 'ko') {
    const isHome = !center;
    const spotId = center ? (center.name || center.id) : '';
    const cleanSpotSlug = encodeURIComponent(spotId);

    // 1. Determine High-Intent Title & Description for Top Search & AI Ranking
    let pageTitle = 'K-TourSpot | 전국 572개 관광안내소 실시간 운영정보 & 스마트 로컬 컨시어지';
    let metaDesc = '대한민국 572개 전국 공식 관광안내소의 실시간 영업상태, 운영시간, 짐보관, 외국어 통역(영/일/중), 무료 Wi-Fi, 스탬프투어 혜택 및 1330 긴급 SOS 허브.';

    if (!isHome) {
      const name = center.translatedName?.[lang] || center.name;
      const addr = center.translatedAddress?.[lang] || center.address;
      const services = center.translatedServices?.[lang] || center.services || '';

      if (lang === 'en') {
        pageTitle = `${name} | Korea Tourist Information Center (Hours, Luggage Storage, Free WiFi, 1330 Hotline)`;
        metaDesc = `Verified travel guide for ${name} located at ${addr}. Real-time operating hours, English consultation, free Wi-Fi, luggage holding, and official 1330 hotline.`;
      } else if (lang === 'ja') {
        pageTitle = `${name} | 韓国観光案内所 (営業時間、手荷物預かり、日本語案内、無料Wi-Fi、スタンプラリー)`;
        metaDesc = `${name}（所在地：${addr}）の公式観光案内。リアルタイム営業時間、日本語通訳相談、無料Wi-Fi、手荷物預かり、緊急1330サポートを提供。`;
      } else if (lang === 'zh') {
        pageTitle = `${name} | 韩国官方旅游咨询处 (实时营业时间、行李寄存、中文翻译、免费Wi-Fi、盖章打卡)`;
        metaDesc = `${name}（地址：${addr}）官方认证旅游向导。提供实时开放时间、中文人工咨询、免费Wi-Fi、行李寄存及1330多语言热线服务。`;
      } else {
        pageTitle = `${name} | 전국 관광안내소 (실시간 운영시간·짐보관·스탬프투어·1330통역·무료 와이파이)`;
        metaDesc = `${name} (${addr})의 실시간 운영시간, 휴무일, 외국어 통역 안내, 편의시설 및 공공데이터 검증 스마트 여행 컨시어지 정보.`;
      }
    }

    // Update document.title
    document.title = pageTitle;

    // 2. Update or Create Meta Tags
    this._setMetaTag('name', 'description', metaDesc);
    this._setMetaTag('property', 'og:title', pageTitle);
    this._setMetaTag('property', 'og:description', metaDesc);
    this._setMetaTag('property', 'og:url', isHome ? `${BASE_URL}/?lang=${lang}` : `${BASE_URL}/?spot=${cleanSpotSlug}&lang=${lang}`);
    this._setMetaTag('property', 'og:type', 'website');
    this._setMetaTag('property', 'og:site_name', 'K-TourSpot');
    this._setMetaTag('property', 'og:image', `${BASE_URL}/icons/icon.svg`);
    this._setMetaTag('name', 'twitter:card', 'summary_large_image');
    this._setMetaTag('name', 'twitter:title', pageTitle);
    this._setMetaTag('name', 'twitter:description', metaDesc);
    this._setMetaTag('name', 'twitter:image', `${BASE_URL}/icons/icon.svg`);

    // Locale Meta Tags
    const localeMap = { ko: 'ko_KR', en: 'en_US', ja: 'ja_JP', zh: 'zh_CN' };
    this._setMetaTag('property', 'og:locale', localeMap[lang] || 'ko_KR');

    // 3. Update Canonical & Bidirectional hreflang links
    this._updateHreflangAndCanonical(spotId, lang);

    // 4. Inject Rich Schema.org JSON-LD (@graph)
    this._injectJsonLdSchema(center, lang);
  }

  /**
   * Updates canonical and hreflang alternate link tags in <head>
   */
  static _updateHreflangAndCanonical(spotId, currentLang) {
    const spotParam = spotId ? `?spot=${encodeURIComponent(spotId)}&` : '?';

    // Remove existing hreflang and canonical
    document.querySelectorAll('link[rel="canonical"], link[rel="alternate"][hreflang]').forEach(el => el.remove());

    const head = document.head;

    // Canonical tag (Self-referential)
    const canonicalLink = document.createElement('link');
    canonicalLink.rel = 'canonical';
    canonicalLink.href = `${BASE_URL}/${spotParam}lang=${currentLang}`;
    head.appendChild(canonicalLink);

    // Alternate hreflang tags
    const hreflangConfigs = [
      { lang: 'ko', code: 'ko' },
      { lang: 'en', code: 'en' },
      { lang: 'ja', code: 'ja' },
      { lang: 'zh', code: 'zh-Hans' },
      { lang: 'en', code: 'x-default' } // Global fallback
    ];

    hreflangConfigs.forEach(({ lang, code }) => {
      const altLink = document.createElement('link');
      altLink.rel = 'alternate';
      altLink.hreflang = code;
      altLink.href = `${BASE_URL}/${spotParam}lang=${lang}`;
      head.appendChild(altLink);
    });
  }

  /**
   * Injects or Updates Schema.org JSON-LD (@graph) for SEO, GEO, and AEO
   */
  static _injectJsonLdSchema(center, lang) {
    let scriptTag = document.getElementById('json-ld-seo');
    if (!scriptTag) {
      scriptTag = document.createElement('script');
      scriptTag.id = 'json-ld-seo';
      scriptTag.type = 'application/ld+json';
      document.head.appendChild(scriptTag);
    }

    if (!center) {
      // Home Page Schemas (WebSite + Organization + WebApplication + HowTo Guide)
      const homeSchema = {
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "WebSite",
            "@id": `${BASE_URL}/#website`,
            "url": BASE_URL,
            "name": "K-TourSpot",
            "description": "Korea National Tourist Information Centers & Smart Local Concierge Hub",
            "inLanguage": ["ko", "en", "ja", "zh-Hans"],
            "potentialAction": {
              "@type": "SearchAction",
              "target": `${BASE_URL}/?search={search_term_string}&lang=ko`,
              "query-input": "required name=search_term_string"
            }
          },
          {
            "@type": "Organization",
            "@id": `${BASE_URL}/#organization`,
            "name": "K-TourSpot",
            "url": BASE_URL,
            "logo": `${BASE_URL}/icons/icon.svg`,
            "sameAs": [
              "https://k-tour.web.app",
              "https://data.go.kr"
            ]
          },
          {
            "@type": "WebApplication",
            "@id": `${BASE_URL}/#webapp`,
            "name": "K-TourSpot (전국 관광안내소 & 스마트 로컬 컨시어지)",
            "url": BASE_URL,
            "applicationCategory": "TravelApplication",
            "operatingSystem": "All",
            "browserRequirements": "Requires JavaScript. Requires HTML5.",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "KRW"
            },
            "featureList": [
              "572 Verified Tourist Information Centers across Korea",
              "Real-time Operating Hours and Holiday Checks",
              "Multilingual Consultation Support (English, Japanese, Chinese, Korean)",
              "Luggage Storage & Local Stamp Tour Hub Directory",
              "Interactive Map with Kakao, Naver, and Google Maps Directions",
              "Smart Travel Scratchpad, Packing Checklist & Expense Ledger",
              "Instant Currency Converter & Survival Korean Phrases"
            ]
          },
          {
            "@type": "HowTo",
            "@id": `${BASE_URL}/#howto-travel`,
            "name": "How to Find Tourist Info, Free Luggage Storage & 24/7 Hotline in South Korea",
            "description": "Step-by-step guide for international and domestic travelers to access certified tourist centers, luggage holding, stamp tours, and multilingual support in Korea.",
            "step": [
              {
                "@type": "HowToStep",
                "name": "Locate Nearest Center",
                "text": "Open K-TourSpot and use GPS 'Near Me' or province filters to find the closest verified tourist info center among 572 national locations."
              },
              {
                "@type": "HowToStep",
                "name": "Check Operating Schedule & Languages",
                "text": "Verify live operating hours (summer/winter), holiday schedule, and on-site foreign language staffing (English, Japanese, Chinese)."
              },
              {
                "@type": "HowToStep",
                "name": "Access Free Luggage Storage & Concierge Perks",
                "text": "Drop baggage at certified holding hubs, collect local stamp tour books for gift certificates, or call 1330 for free instant telephone interpretation."
              }
            ]
          }
        ]
      };
      scriptTag.textContent = JSON.stringify(homeSchema, null, 2);
      return;
    }

    // Individual Spot Schema (TouristInformationCenter + FAQPage + BreadcrumbList)
    const name = center.translatedName?.[lang] || center.name;
    const address = center.translatedAddress?.[lang] || center.address;
    const spotSlug = encodeURIComponent(center.name || center.id);
    const spotUrl = `${BASE_URL}/?spot=${spotSlug}&lang=${lang}`;

    // Generate Natural Language FAQ for AEO Answer Engine Citation
    const faqList = GeoFaqGenerator.generateFaq(center, lang);

    const schemaGraph = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "TouristInformationCenter",
          "@id": `${spotUrl}#place`,
          "name": `${name} (${center.name})`,
          "alternateName": [
            center.translatedName?.en || center.name,
            center.translatedName?.ja || center.name,
            center.translatedName?.zh || center.name
          ],
          "description": `${name} provides official multilingual travel guides, maps, local consultation, luggage storage, and smart tourist assistance in Korea.`,
          "url": spotUrl,
          "telephone": center.phone || "+82-2-1330",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": address,
            "addressRegion": center.province || "Korea",
            "addressCountry": "KR"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": center.lat || 37.5665,
            "longitude": center.lng || 126.9780
          },
          "hasMap": `https://www.google.com/maps/dir/?api=1&destination=${center.lat},${center.lng}`,
          "openingHoursSpecification": [
            {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
              "opens": center.summerOpen || "09:00",
              "closes": center.summerClose || "18:00"
            }
          ],
          "availableLanguage": [
            { "@type": "Language", "name": "Korean" },
            ...(center.languages?.en ? [{ "@type": "Language", "name": "English" }] : []),
            ...(center.languages?.ja ? [{ "@type": "Language", "name": "Japanese" }] : []),
            ...(center.languages?.zh ? [{ "@type": "Language", "name": "Chinese" }] : [])
          ],
          "amenityFeature": [
            { "@type": "LocationFeatureSpecification", "name": "Multilingual Guides", "value": true },
            ...(center.facilities?.wifi ? [{ "@type": "LocationFeatureSpecification", "name": "Free Wi-Fi", "value": true }] : []),
            ...(center.facilities?.luggage ? [{ "@type": "LocationFeatureSpecification", "name": "Luggage Storage", "value": true }] : []),
            ...(center.facilities?.wheelchair ? [{ "@type": "LocationFeatureSpecification", "name": "Wheelchair Accessible", "value": true }] : []),
            ...(center.facilities?.stamp ? [{ "@type": "LocationFeatureSpecification", "name": "Stamp Tour", "value": true }] : [])
          ],
          "isAccessibleForFree": true,
          "priceRange": "Free",
          "publicAccess": true,
          "dateModified": "2026-08-15",
          "provider": {
            "@type": "Organization",
            "name": "K-TourSpot",
            "url": BASE_URL
          }
        },
        {
          "@type": "BreadcrumbList",
          "@id": `${spotUrl}#breadcrumb`,
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "K-TourSpot",
              "item": `${BASE_URL}/?lang=${lang}`
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": center.province || "Korea",
              "item": `${BASE_URL}/?province=${encodeURIComponent(center.province || '')}&lang=${lang}`
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": name,
              "item": spotUrl
            }
          ]
        },
        {
          "@type": "FAQPage",
          "@id": `${spotUrl}#faq`,
          "mainEntity": faqList.map(item => ({
            "@type": "Question",
            "name": item.question,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": item.answer
            }
          }))
        }
      ]
    };

    scriptTag.textContent = JSON.stringify(schemaGraph, null, 2);
  }

  static _setMetaTag(keyAttr, keyVal, content) {
    let tag = document.querySelector(`meta[${keyAttr}="${keyVal}"]`);
    if (!tag) {
      tag = document.createElement('meta');
      tag.setAttribute(keyAttr, keyVal);
      document.head.appendChild(tag);
    }
    tag.setAttribute('content', content);
  }
}
